import type { Booking } from "@/logic/api";
import { CollectionDeliveryStatus, VehicleStatus } from "@/logic/api";
import { countVehicleIssues } from "@/utility/assetIssues";
import { formatVehicleWithoutColour } from "@/utility/utils";

const cardDate = (value: Date | string | null | undefined) =>
  value instanceof Date ? value.toJSON() : value;

const firstAnprScanDateTime = (booking: Booking): string | null => {
  if (booking.vehicle?.statusId !== VehicleStatus.NotCheckedIn) return null;

  let firstScan: string | null = null;
  let firstTime = Number.POSITIVE_INFINITY;
  for (const scan of booking.anprScanHistory ?? []) {
    const time = new Date(scan.scanDate).getTime();
    if (firstScan == null || time < firstTime) {
      firstScan = scan.scanDate;
      firstTime = time;
    }
  }
  return firstScan;
};

// BookingListItem and the live diff consume the same booking-derived values.
// The component decides which of these to show for its profile, flags and size.
export const getBookingCardValues = (booking: Booking) => {
  const vehicle = booking.vehicle;
  const isCheckedIn = vehicle?.statusId === VehicleStatus.CheckedIn;
  const keyLocation = isCheckedIn ? booking.vehicleKey?.location : null;
  const vehicleLocation = isCheckedIn ? vehicle?.location : null;
  const trackingId = booking.trackingId?.trim();
  const collectionDeliveryStatus = vehicle?.collectionDeliveryStatus;

  return {
    statusId: booking.statusId,
    vehicleStatusId: vehicle?.statusId ?? VehicleStatus.NotCheckedIn,
    ref: booking.ref,
    altRef: booking.altRef,
    bookingReference:
      isCheckedIn && trackingId ? `${booking.ref} / ${trackingId}` : booking.ref,
    fullName: booking.leadContact?.fullName,
    shortName: booking.leadContact?.shortName,
    packageReference: booking.packageRef?.trim() || booking.packageCode || "",
    legacyPackageReference:
      booking.packageRef == null ? booking.packageCode : booking.packageRef,
    vip: booking.vip,
    assistance: booking.assistance,
    valet: booking.valet,
    collection: booking.collection,
    delivery: booking.delivery,
    bookingStartDate: booking.bookingStartDate,
    expectedReturnDate: isCheckedIn ? booking.expectedReturnDate : "",
    confirmedArrivalDate: cardDate(booking.confirmedArrivalDate),
    collectionRequestedAt:
      booking.collection &&
      collectionDeliveryStatus === CollectionDeliveryStatus.CollectionRequested
        ? cardDate(booking.collectionRequestedAt)
        : null,
    collectionCompletedAt:
      booking.collection &&
      collectionDeliveryStatus === CollectionDeliveryStatus.CollectionComplete
        ? cardDate(booking.collectionCompletedAt)
        : null,
    deliveryRequestedAt:
      booking.delivery &&
      (collectionDeliveryStatus === CollectionDeliveryStatus.DeliveryRequested ||
        collectionDeliveryStatus === CollectionDeliveryStatus.DeliveryInProgress)
        ? cardDate(booking.deliveryRequestedAt)
        : null,
    deliveryCompletedAt:
      booking.delivery &&
      collectionDeliveryStatus === CollectionDeliveryStatus.DeliveryComplete
        ? cardDate(booking.deliveryCompletedAt)
        : null,
    outboundFlightDate: booking.outboundFlight?.flightDate,
    returnFlightNumber: isCheckedIn ? booking.returnFlight?.flightNumber : null,
    vehicleRegistration: vehicle?.registration,
    vehicleColour: vehicle?.colour,
    vehicleFuelType: vehicle?.fuelType,
    vehicleCheckInDate: vehicle?.checkInDate ?? "",
    collectionDeliveryStatus,
    vehicleLocationPresent: vehicleLocation != null,
    vehicleLocationName: vehicleLocation?.name,
    vehicleLocationParentName: vehicleLocation?.parentName,
    keyLocationPresent: keyLocation != null,
    keyLocationName: keyLocation?.name,
    keyLocationParentName: keyLocation?.parentName,
    keyLocationType: keyLocation?.type,
    get vehicleDescription() {
      return vehicle == null ? "" : formatVehicleWithoutColour(vehicle);
    },
    get hasVehicleIssues() {
      return Boolean(countVehicleIssues(booking.vehicleAuditIssues));
    },
    get firstAnprScanDateTime() {
      return firstAnprScanDateTime(booking);
    },
  };
};

export type BookingCardValues = ReturnType<typeof getBookingCardValues>;
